// @ts-nocheck
/**
 * Roster packet & player export.
 *
 * The operator sees what the file will contain *before* it exists: how many
 * teams have actually submitted a roster, how many players that is, and which
 * teams would show up empty. A packet that is two thirds blank still looks
 * finished once it is a PDF in a college coach's inbox, so the counts come
 * first and the download buttons come after them.
 *
 * All three actions live on the deployed rle-event-packet function; nothing
 * here generates a file locally beyond turning the returned string into a Blob.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertTriangle,
  ChevronDown,
  Download,
  FileText,
  Loader2,
  Printer,
} from "lucide-react";
import { toast } from "sonner";

type PacketError = { code?: string; message?: string; can_force?: boolean; status?: number };

/**
 * The function reports its refusals in the body ({ code, message, can_force }),
 * which supabase-js hides behind its generic non-2xx string — so read it back
 * off the Response before deciding what to show.
 */
async function callPacket(body: Record<string, unknown>): Promise<{ data: any; err: PacketError | null }> {
  const { data, error } = await supabase.functions.invoke("rle-event-packet", { body });
  if (!error) return { data, err: null };

  let parsed: PacketError = { message: error.message };
  try {
    const ctx = (error as any).context;
    if (ctx && typeof ctx.json === "function") {
      const res = typeof ctx.clone === "function" ? ctx.clone() : ctx;
      const json = await res.json().catch(() => null);
      if (json) {
        parsed = {
          code: json.code ?? json.error?.code,
          message: json.message ?? json.error?.message ?? json.error,
          can_force: json.can_force ?? json.error?.can_force,
          status: ctx.status,
        };
      } else {
        parsed.status = ctx.status;
      }
    }
  } catch {
    /* fall through with the generic message */
  }
  return { data: null, err: parsed };
}

function downloadBlob(content: string, filename: string, contentType: string) {
  const blob = new Blob([content], { type: `${contentType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export default function EventPacketDialog({
  eventId,
  open,
  onOpenChange,
}: {
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [err, setErr] = useState<PacketError | null>(null);
  const [includeEmpty, setIncludeEmpty] = useState(true);
  const [busy, setBusy] = useState<null | "csv" | "html">(null);
  const [missingOpen, setMissingOpen] = useState(false);

  const loadPreview = useCallback(
    async (force = false) => {
      setLoading(true);
      setErr(null);
      const { data, err } = await callPacket({
        action: "preview",
        event_id: eventId,
        include_empty_teams: includeEmpty,
        ...(force ? { force: true } : {}),
      });
      if (err) {
        setErr(err);
        setPreview(null);
      } else {
        setPreview(data);
      }
      setLoading(false);
    },
    [eventId, includeEmpty]
  );

  // Re-assert the deliberate default every time the dialog opens so an
  // operator who unchecked it once doesn't quietly get a different export
  // for the rest of the session.
  useEffect(() => {
    if (open) {
      setIncludeEmpty(true);
      // If the box was already true, the includeEmpty effect below won't fire,
      // so load the preview directly.
      if (includeEmpty) loadPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeEmpty]);

  const generate = async (action: "csv" | "html", force = false) => {
    if (busy) return;
    setBusy(action);
    setErr(null);
    const { data, err } = await callPacket({
      action,
      event_id: eventId,
      include_empty_teams: includeEmpty,
      ...(force ? { force: true } : {}),
    });
    setBusy(null);

    if (err) {
      setErr(err);
      return;
    }

    const filename = data?.filename ?? `event-${action === "csv" ? "rosters.csv" : "packet.html"}`;

    if (action === "csv") {
      // A storage failure is not the operator's problem when the bytes are here.
      if (data?.content) downloadBlob(data.content, filename, data.content_type || "text/csv");
      else if (data?.url) window.open(data.url, "_blank", "noopener");
      else toast.error("The export came back empty");
      return;
    }

    const win = window.open("", "_blank", "noopener");
    if (win && data?.content) {
      win.document.open();
      win.document.write(data.content);
      win.document.close();
    } else if (data?.url) {
      // Popup blocked, or the file was too large to inline.
      window.open(data.url, "_blank", "noopener");
    } else if (!win) {
      toast.error("Your browser blocked the packet window — allow pop-ups and try again");
    }
  };

  const totals = preview?.totals;
  const missingTeams = (preview?.teams ?? []).filter((t: any) => !t.has_roster);
  const unauthenticated = err?.code === "UNAUTHENTICATED" || err?.status === 401;
  const rlDown = err?.code === "RECRUITLOOK_UNAVAILABLE" || err?.can_force;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" /> Export rosters & players
          </DialogTitle>
          <DialogDescription>
            {preview?.event?.name
              ? `${preview.event.name}${preview.event.dates ? ` · ${preview.event.dates}` : ""}`
              : "One row per player, plus every team's coach contact."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking what's in the packet…
          </div>
        ) : unauthenticated ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>You're not an operator of this event</AlertTitle>
            <AlertDescription>
              {err?.message || "Ask an organisation admin to run this export for you."}
            </AlertDescription>
          </Alert>
        ) : rlDown ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>RecruitLook didn't answer</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>
                {err?.message ||
                  "Team names and rosters live on RecruitLook. Without it the packet would be teams with no names and no players."}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => loadPreview()} disabled={loading}>
                  Try again
                </Button>
                <Button size="sm" variant="secondary" onClick={() => generate("csv", true)} disabled={!!busy}>
                  {busy === "csv" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Download coach contacts anyway
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        ) : err ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Couldn't check the packet</AlertTitle>
            <AlertDescription>{err.message || "Please try again."}</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            <p className="text-sm">
              <span className="font-semibold">
                {totals?.teams_with_roster ?? 0} of {totals?.teams ?? 0} teams
              </span>{" "}
              have submitted a roster — {totals?.players ?? 0} players.
              {includeEmpty && (totals?.teams_without_roster ?? 0) > 0 && (
                <>
                  {" "}
                  {totals.teams_without_roster} teams will appear with their coach contact and
                  &ldquo;No roster submitted&rdquo;.
                </>
              )}
            </p>

            {totals?.teams_missing_rl_link > 0 && (
              <p className="text-sm text-amber-700">
                {totals.teams_missing_rl_link} team
                {totals.teams_missing_rl_link === 1 ? " is" : "s are"} not linked to RecruitLook, so
                {totals.teams_missing_rl_link === 1 ? " its" : " their"} roster can't be pulled.
              </p>
            )}

            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={includeEmpty}
                onCheckedChange={(v) => setIncludeEmpty(v === true)}
                className="mt-0.5"
              />
              <span>Include teams with no roster</span>
            </label>

            {missingTeams.length > 0 && (
              <Collapsible open={missingOpen} onOpenChange={setMissingOpen}>
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm font-medium"
                  >
                    <span>{missingTeams.length} teams still missing a roster</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${missingOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <ul className="mt-2 max-h-56 space-y-1 overflow-auto rounded-md border p-2 text-sm">
                    {missingTeams.map((t: any) => (
                      <li key={t.registration_id} className="flex flex-wrap justify-between gap-2">
                        <span>
                          {t.team_name}
                          {t.division ? ` · ${t.division}` : ""}
                        </span>
                        <span className="text-muted-foreground">
                          {[t.coach_name, t.coach_email].filter(Boolean).join(" · ")}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => generate("html")}
              disabled={!preview || !!busy || loading}
            >
              {busy === "html" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Printer className="mr-2 h-4 w-4" />
              )}
              Open printable packet
            </Button>
            <Button onClick={() => generate("csv")} disabled={!preview || !!busy || loading}>
              {busy === "csv" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Download CSV
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
